import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { HAND_COMBO_METADATA } from '@solante/shared';

interface ComboReferenceProps {
  isOpen: boolean;
  onToggle: () => void;
}

const pokerEquivalent: Record<string, string> = {
  'exit-liquidity': 'High Card',
  'twin-rugs': 'Pair',
  'double-bottom': 'Two Pair',
  'triple-top': 'Three of a Kind',
  'bull-run': 'Straight',
  'chain-maxi': 'Flush',
  'vc-full-bag': 'Full House',
  'sybil-attack': 'Four of a Kind',
  'the-singularity': 'Straight Flush',
};

const suits = [
  { name: 'Sol', symbol: '\u2600', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  { name: 'Mint', symbol: '\u224B', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { name: 'Burn', symbol: '\u2726', color: 'text-pink-400', bg: 'bg-pink-500/10' },
  { name: 'Stake', symbol: '\u2605', color: 'text-purple-400', bg: 'bg-purple-500/10' },
];

// Sorted strongest → weakest for reference display
const combosDescending = [...HAND_COMBO_METADATA].reverse();

export const ComboReference: React.FC<ComboReferenceProps> = ({ isOpen, onToggle }) => {
  return (
    <>
      {/* Floating toggle button */}
      {!isOpen && (
        <motion.button
          type="button"
          onClick={onToggle}
          initial={{ opacity: 0, x: 20 }}
          animate={{
            opacity: 1,
            x: 0,
            boxShadow: [
              '0 0 8px rgba(0,255,255,0.2)',
              '0 0 16px rgba(0,255,255,0.4)',
              '0 0 8px rgba(0,255,255,0.2)',
            ],
          }}
          transition={{
            opacity: { duration: 0.3 },
            x: { duration: 0.3 },
            boxShadow: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
          }}
          className="fixed bottom-20 right-4 z-40 flex items-center gap-2 rounded-full border border-arcade-cyan/50 bg-black/80 px-4 py-2.5 text-sm font-semibold text-arcade-cyan backdrop-blur hover:border-arcade-cyan/80 hover:text-white transition-colors md:bottom-6"
        >
          <BookOpenIcon className="h-5 w-5" />
          Combo Guide
        </motion.button>
      )}

      {/* Drawer backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[45] bg-black/40"
            onClick={onToggle}
          />
        )}
      </AnimatePresence>

      {/* Drawer panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            className="fixed right-0 top-0 bottom-0 z-[46] w-80 max-w-[90vw] overflow-y-auto border-l border-white/10 bg-black/95 backdrop-blur-md"
          >
            <div className="p-4 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-bold text-white">Combo Guide</h2>
                <button
                  type="button"
                  onClick={onToggle}
                  className="rounded-lg p-1.5 text-white/40 hover:bg-white/10 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Combos list */}
              <div className="space-y-2">
                {combosDescending.map((combo, i) => (
                  <div
                    key={combo.comboId}
                    className="rounded-lg border border-white/10 bg-white/5 px-3 py-2"
                  >
                    <div className="flex items-baseline justify-between">
                      <div>
                        <span className="text-sm font-semibold text-white">{combo.name}</span>
                        <span className="ml-2 text-[10px] text-white/40">
                          {pokerEquivalent[combo.comboId]}
                        </span>
                      </div>
                      <span className="text-[10px] font-mono text-white/30">
                        #{combosDescending.length - i}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs">
                      <span className="text-arcade-cyan">{combo.score} pts</span>
                      <span className="text-white/30">&times;</span>
                      <span className="text-arcade-pink">{combo.multiplier.toFixed(1)}x</span>
                      <span className="text-white/30">=</span>
                      <span className="font-semibold text-white/70">
                        {Math.round(combo.score * combo.multiplier)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Suits section */}
              <div className="space-y-2 pt-2 border-t border-white/10">
                <h3 className="text-xs uppercase tracking-widest text-white/40">Suits</h3>
                <div className="grid grid-cols-2 gap-2">
                  {suits.map((suit) => (
                    <div
                      key={suit.name}
                      className={`rounded-lg border border-white/10 ${suit.bg} px-3 py-2 text-center`}
                    >
                      <span className={`text-xl ${suit.color}`}>{suit.symbol}</span>
                      <div className={`text-xs font-semibold ${suit.color}`}>{suit.name}</div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-white/30 text-center">
                  5 cards of the same suit = Chain Maxi (Flush)
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
